/* RK deck API client (W4) — thin wrappers over the W3 endpoints plus the
 * existing ruled verbs the deck cards dispatch to (merge / keep-separate /
 * snooze / confirm-pre-existing).
 *
 * Flag detection: the /api/rk/decks namespace 404s while `rk_decks` is off
 * (dark ship), so a probe on the queue endpoint IS the flag read — no
 * config endpoint round-trip needed. */

import { useEffect, useState } from 'react'
import { apiFetch, ApiError } from '../../lib/api'
import type {
  ApproveDocsResponse,
  ApproveDocsRow,
  BookPlugResponse,
  DeckQueueResponse,
  DeckResponse,
  WalkOutcome,
  WalkResolveResponse,
} from './types'

/* `background`: a heartbeat re-fetch, not the officer — never moves the
 * idle clock (lib/api.ts). */
export async function fetchDeckQueue(
  fundId?: string | null,
  background = false
): Promise<DeckQueueResponse> {
  const qs = fundId ? `?fund_id=${encodeURIComponent(fundId)}` : ''
  return apiFetch<DeckQueueResponse>(`/api/rk/decks${qs}`, { background })
}

/** deck_id is namespaced: "group:<uuid>" | "group:<uuid>:<n>" (split
 * documents — one deck per chain segment, n is the 1-based segment
 * index) | "task:<uuid>". */
export async function fetchDeck(deckId: string): Promise<DeckResponse> {
  const [kind, id, segment] = deckId.split(':')
  const path =
    kind === 'group'
      ? `/api/rk/decks/group/${id}${segment ? `?segment=${segment}` : ''}`
      : `/api/rk/decks/task/${id}`
  return apiFetch<DeckResponse>(path)
}

export async function approveDocs(
  groupId: string,
  rows: ApproveDocsRow[],
  /** The deck_version the officer decided against (snapshot isolation,
   * Ale 2026-09-02). When stale the server refuses with 409 deck_changed
   * and nothing books — the caller re-opens the card and re-confirms. */
  deckVersion?: string | null
): Promise<ApproveDocsResponse> {
  return apiFetch<ApproveDocsResponse>(`/api/rk/decks/group/${groupId}/approve-docs`, {
    method: 'POST',
    body: JSON.stringify(deckVersion ? { rows, deck_version: deckVersion } : { rows }),
  })
}

export async function bookPlug(
  taskId: string,
  body?: { units_delta?: string; note?: string }
): Promise<BookPlugResponse> {
  return apiFetch<BookPlugResponse>(`/api/rk/decks/task/${taskId}/book-plug`, {
    method: 'POST',
    body: JSON.stringify(body ?? {}),
  })
}

/** §914a opening — the reco card's plug for a holding that pre-dates the
 * record (writes the pre_existing_ownership trade). Wave 3 D4: no
 * attestation — same rank as bookPlug; `reason` is an optional note kept
 * for API compatibility. Ordinary plugs go through bookPlug instead. */
export async function confirmPreExisting(taskId: string, reason?: string) {
  return apiFetch(`/api/tasks/${taskId}/confirm-pre-existing`, {
    method: 'POST',
    body: JSON.stringify(reason?.trim() ? { reason: reason.trim() } : {}),
  })
}

export type SnoozeMode = 'next_document' | 'timer'

export async function snoozeTask(taskId: string, mode: SnoozeMode, until?: string) {
  return apiFetch(`/api/tasks/${taskId}/snooze`, {
    method: 'POST',
    body: JSON.stringify(until ? { mode, until } : { mode }),
  })
}

/** The walk card's ONE Confirm (L-series). Dispatches to the same ruled
 * look-through write path the tasks surface uses.
 *
 * ⚠ It never mints a document request: when the chain reads incomplete
 * after `walk_through`, the response says so and the request is offered
 * from Chase & Request — the one chase source. */
export async function resolveWalk(
  taskId: string,
  outcome: WalkOutcome,
  note?: string
): Promise<WalkResolveResponse> {
  return apiFetch<WalkResolveResponse>(`/api/rk/decks/task/${taskId}/walk`, {
    method: 'POST',
    body: JSON.stringify(note ? { outcome, note } : { outcome }),
  })
}
/** R7 near-match (audit F5 / U3): the officer's answer to the
 * trade-duplicate question. Dispatches to the SAME ruled write path the
 * tasks surface uses — `POST /api/tasks/{id}/resolve` — so the deck adds
 * no second lineage. The outcome is MANDATORY: the handler 422s on a
 * missing one, because auto-merge (a silently understated holding) and
 * auto-duplicate (a loud value break) are both forbidden. */
export type TradeDuplicateOutcome = 'same_trade' | 'new_trade'

export async function resolveTradeDuplicate(taskId: string, outcome: TradeDuplicateOutcome) {
  return apiFetch(`/api/tasks/${taskId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ outcome }),
  })
}

export interface MergeChoice {
  source_proposal_ids: string[]
  target_proposal_ids?: string[]
  target_instrument_id?: string
  target_entity_id?: string
  canonical_name?: string
}

export async function mergeObjects(body: MergeChoice) {
  return apiFetch('/api/proposals/objects/merge', { method: 'POST', body: JSON.stringify(body) })
}

/** Cold-start founding verb (2026-08-18): materialize a create_entity
 * proposal as the Fund — the same idempotent endpoint ChainReviewPage
 * uses, now reachable from the deck. */
export async function markFundVehicle(groupId: string, proposalId: string) {
  return apiFetch<{ fund_id: string; fund_name: string; created: boolean }>(
    `/api/proposals/${groupId}/proposals/${proposalId}/mark-fund-vehicle`,
    { method: 'POST', body: JSON.stringify({}) }
  )
}

export async function keepSeparate(sourceProposalIds: string[]) {
  return apiFetch('/api/proposals/objects/keep-separate', {
    method: 'POST',
    body: JSON.stringify({ source_proposal_ids: sourceProposalIds }),
  })
}

/* ── flag probe ──────────────────────────────────────────────────────── */

let probe: Promise<boolean> | null = null

/** Cheap namespace admission; the server retains the existing due-snooze sweep. */
export function probeRkDecks(): Promise<boolean> {
  if (!probe) {
    probe = apiFetch<{ enabled: true }>('/api/rk/decks/capabilities')
      .then(() => true)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) return false
        // Auth/network hiccups must not latch the flag off for the session.
        probe = null
        return false
      })
  }
  return probe
}

/** null while probing, then the flag. */
export function useRkDecksEnabled(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  useEffect(() => {
    let cancelled = false
    probeRkDecks().then((on) => {
      if (!cancelled) setEnabled(on)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return enabled
}
